<template>
  <PdfRedactingPanelWrapper data-locator="redact-by-ai-card">
    <template #title>
      <div class="d-flex align-center">
        <img
          :src="redactWithProtegeIcon"
          height="20"
          width="20"
          alt="Icon"
          class="mr-2"
        />
        <h3 class="text-grey-900 text--small">
          {{ $t('common.PdfRedactingSidebar.searchByAi.title') }}
        </h3>
      </div>

      <AppButton
        size="small"
        type="outlined"
        variant="neutral"
        :disabled="searchByAIPending || !pdfHasText"
        @click="searchEntitiesByAi"
      >
        {{ $t('common.search') }}
      </AppButton>
    </template>

    <template #content>
      <v-skeleton-loader
        v-if="searchByAIPending || isPendingSearches"
        type="card"
      />
      <template v-else>
        <div
          v-if="!searchEntitiesStarted"
          class="d-flex flex-column justify-center align-center"
        >
          <v-img :src="redactWithProtegeIcon" width="32" class="my-4" />

          <div class="mx-4 text-center">
            <i18n-t
              keypath="common.PdfRedactingSidebar.searchByAi.description"
              tag="span"
              scope="global"
              class="text--small text-center mt-4"
            >
              <template #search>
                <strong>{{ $t('common.search') }}</strong>
              </template>
            </i18n-t>

            <AppInfoMenu>
              <div class="text--x-small font-weight-semi-bold mb-3">
                {{ $t('common.PdfRedactingSidebar.searchByAi.entitiesTitle') }}
              </div>
              <ul class="pl-5 text--x-small">
                <li
                  v-for="(entity, key) in entitiesList"
                  :key="key"
                >
                  {{ entity.label }}
                </li>
              </ul>
            </AppInfoMenu>
          </div>
        </div>
        <div v-else>
          <p v-if="searchByAIError" class="text-error text-center">
            {{ $t('common.PdfRedactingSidebar.searchByAi.error') }}
          </p>
          <template v-else>
            <template v-if="hasEntities">
              <span class="text--small">
                {{ $t('common.PdfRedactingSidebar.searchByAi.info') }}
              </span>

              <v-list class="px-0" hide-details>
                <!-- Entities -->
                <template v-for="(entity, key) in filteredEntities" :key="key">
                  <v-list-item class="px-0">
                    <div class="d-flex align-center">
                      <AppButton
                        size="x-small"
                        type="icon"
                        @click="collapseEntity(key)"
                      >
                        <app-icon-ds
                          :icon="collapsedEntities[key] ? 'chevron-right' : 'chevron-down'"
                          size="xxs"
                        />
                      </AppButton>
                      <v-checkbox
                        v-model="selectedEntities[key]"
                        :indeterminate="!!selectedEntities[key]?.indeterminate"
                        hide-details
                        @update:modelValue="SelectEntity(key)"
                      >
                        <template #label>
                          <AppTooltip>
                            <template #activator="{ props }">
                              <div
                                class="checkbox-label text-truncate"
                                v-bind="props"
                              >
                                {{ entity.label }}
                              </div>
                            </template>
                            <span>{{ entity.label }}</span>
                          </AppTooltip>
                          <span class="ml-1">({{ entity.items.length }})</span>
                        </template>
                      </v-checkbox>
                    </div>
                  </v-list-item>

                  <!-- Entity items -->
                  <v-list-item
                    v-for="(item, index) in entity.items"
                    v-show="!collapsedEntities[key]"
                    class="pl-12 pr-0"
                    :key="index"
                  >
                    <v-checkbox
                      v-model="selectedEntityItems[item.text]"
                      hide-details
                      @update:modelValue="SelectEntityItem(key)"
                    >
                      <template #label>
                        <AppTooltip>
                          <template #activator="{ props }">
                            <div
                              class="checkbox-label text-truncate"
                              v-bind="props"
                            >
                              {{ item.text }}
                            </div>
                          </template>
                          <span>{{ item.text }}</span>
                        </AppTooltip>
                        <span class="ml-2">({{ item.count }})</span>
                      </template>
                    </v-checkbox>
                  </v-list-item>
                </template>
              </v-list>
            </template>

            <!-- No results -->
            <div
              v-else
              class="d-flex flex-column justify-center align-center"
            >
              <v-img :src="redactWithProtegeIcon" width="32" class="my-4" />
              <span>{{ $t('common.PdfRedactingSidebar.searchByAi.noResults1') }}</span>
              <span>{{ $t('common.PdfRedactingSidebar.searchByAi.noResults2') }}</span>
            </div>
          </template>
        </div>
      </template>
    </template>
  </PdfRedactingPanelWrapper>
</template>

<script>
import { mapState, mapActions } from 'vuex'

import AppInfoMenu from '@/common/AppInfoMenu.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import AppButton from '@/design-system/buttons/AppButton.vue'
import { DocumentViewerActionTypes } from '@/store/modules/document-viewer/action_types'

import PdfRedactingPanelWrapper from './PdfRedactingPanelWrapper.vue'
import { SEARCH_TYPES } from '../constants'
import { convertMatches, renderMatches, getOriginalIndex, match, normalize } from '../pdf-document-search.js'

export default {
  name: 'PdfRedactingSidebarContentAI',
  components: {
    AppInfoMenu,
    AppTooltip,
    AppButton,
    PdfRedactingPanelWrapper,
  },
  emits: ['updateRectangles'],
  props: {
    fileId: {
      type: String,
      required: true,
    },
    mnemo: {
      type: String,
      required: true,
    },
    isPendingSearches: {
      type: Boolean,
      default: false,
    },
    pdfHasText: {
      required: true,
      type: Boolean,
    },
    pdfAppInstance: {
      required: true,
      type: Object,
    },
  },
  data() {
    return {
      searchEntitiesStarted: false,
      selectedEntities: {},
      selectedEntityItems: {},
      collapsedEntities: {},
      hasLoadedEntities: false,
      pageContents: [],
      pageDiffs: [],
      hasDiacritics: [],
      searchResultsMap: new Map(),
    }
  },
  computed: {
    ...mapState('documentViewer', ['searchByAIPending', 'searchByAIError', 'searchByAIResults']),
    redactWithProtegeIcon() {
      return new URL('@/assets/img/redact-by-keywords-ai-no-results.svg', import.meta.url).href
    },
    entitiesWithCount() {
      const { results, summary } = this.searchByAIResults
      if (!results) return {}
      const grouped = {}

      // Group entities by type and text
      for (const [type, items] of Object.entries(results)) {
        grouped[type] = {}
        items.forEach(item => {
          if (!grouped[type][item.text]) {
            grouped[type][item.text] = {
              text: item.text,
              type,
              ids: [],
              count: 0,
            }
          }
          grouped[type][item.text].ids.push(item.id)
        })
      }

      // Apply counts from summary
      if (summary) {
        for (const [type, blocks] of Object.entries(grouped)) {
          const summaryBlock = summary[type]
          for (const obj of Object.values(blocks)) {
            if (summaryBlock?.values) {
              obj.count = summaryBlock.values.filter(id =>
                obj.ids.includes(id),
              ).length
            } else {
              obj.count = obj.ids.length
            }
          }
        }
      } else {
        // fallback if no summary
        for (const blocks of Object.values(grouped)) {
          for (const obj of Object.values(blocks)) {
            obj.count = obj.ids.length
          }
        }
      }
      return grouped
    },
    hasEntities() {
      return Object.values(this.entitiesWithCount || {})
        .some(typeBlock => Object.keys(typeBlock).length > 0)
    },
    entitiesList() {
      const entities = this.$tm('common.PdfRedactingSidebar.searchByAi.entities') || {}
      return Object.keys(entities).reduce((acc, key) => {
        acc[key] = {
          label: this.$t(`common.PdfRedactingSidebar.searchByAi.entities.${key}`, key),
          value: entities[key],
        }
        return acc
      }, {})
    },
    filteredEntities() {
      const output = {}
      for (const [type, groupedBlocks] of Object.entries(this.entitiesWithCount)) {
        const items = Object.values(groupedBlocks)
        if (items.length > 0) {
          output[type] = {
            label: this.$t(`common.PdfRedactingSidebar.searchByAi.entities.${type}`, type),
            items,
          }
        }
      }
      return output
    },
  },
  methods: {
    ...mapActions('documentViewer', [DocumentViewerActionTypes.SEARCH_BY_AI]),
    async searchEntitiesByAi() {
      this.searchEntitiesStarted = true
      if (this.hasLoadedEntities && !this.searchByAIError) {
        await this.autoSelectAllEntities()
        return
      }
      try {
        await this.SEARCH_BY_AI({
          mnemo: this.mnemo,
          fileId: this.fileId,
        })

        this.hasLoadedEntities = true
        await this.autoSelectAllEntities()
      } catch (error) {
        console.error('Error searching by AI', error)
      }
    },
    async autoSelectAllEntities() {
      console.log('[AI Redacting] Auto-selecting all entities')
      const allItems = []
      
      for (const entityKey of Object.keys(this.filteredEntities)) {
        this.selectedEntities[entityKey] = true
        // Collect all items
        for (const item of this.filteredEntities[entityKey].items) {
          this.selectedEntityItems[item.text] = true
          allItems.push(item.text)
        }
      }
      
      console.log(`[AI Redacting] Processing ${allItems.length} total items`)
      
      // Search for all items
      for (const itemText of allItems) {
        if (!this.searchResultsMap.has(itemText)) {
          await this.searchOccurrences(itemText)
        }
      }
      this.updateRedactedZones()
      
    },
    async SelectEntity(entityKey) {
      const isSelected = this.selectedEntities[entityKey]
      const items = this.filteredEntities[entityKey]?.items || []
      
      console.log(`[AI Redacting] Entity "${entityKey}" ${isSelected ? 'selected' : 'deselected'} - Processing ${items.length} items`)
      
      for (const item of items) {
        this.selectedEntityItems[item.text] = isSelected
        
        if (isSelected) {
          if (!this.searchResultsMap.has(item.text)) {
            console.log(`[AI Redacting] Drawing rectangles for entity item: "${item.text}"`)
            await this.searchOccurrences(item.text)
          }
        } else {
          console.log(`[AI Redacting] Removing rectangles for entity item: "${item.text}"`)
          this.removeSearchResult(item.text)
        }
      }
      this.updateRedactedZones()
    },
    async SelectEntityItem(entityKey) {
      const items = this.filteredEntities[entityKey].items
      const total = items.length
      const checked = items.filter(i => this.selectedEntityItems[i.text]).length
      if (checked === 0) {
        this.selectedEntities[entityKey] = false
      } else if (checked === total) {
        this.selectedEntities[entityKey] = true
      } else {
        this.selectedEntities[entityKey] = { indeterminate: true }
      }
      
      // Handle rectangle drawing
      const changedItems = items.filter(item => {
        const isSelected = this.selectedEntityItems[item.text]
        const hasSearchResult = this.searchResultsMap.has(item.text)
        return isSelected !== hasSearchResult
      })
      
      console.log(`[AI Redacting] Entity item state changed in "${entityKey}" - ${changedItems.length} items to update`)
      
      for (const item of changedItems) {
        if (this.selectedEntityItems[item.text]) {
          console.log(`[AI Redacting] Drawing rectangles for: "${item.text}"`)
          await this.searchOccurrences(item.text)
        } else {
          console.log(`[AI Redacting] Removing rectangles for: "${item.text}"`)
          this.removeSearchResult(item.text)
        }
      }
      this.updateRedactedZones()
    },

    collapseEntity(key) {
      this.collapsedEntities[key] = !this.collapsedEntities[key]
    },
    async searchOccurrences(query) {
      if (!query || !this.pdfAppInstance?.pdfDocument) {
        console.warn(`[AI Redacting] Cannot search - query: "${query}", pdfDocument available: ${!!this.pdfAppInstance?.pdfDocument}`)
        return
      }
      if (this.searchResultsMap.has(query)) {
        console.log(`[AI Redacting] Query "${query}" already searched, skipping`)
        return
      }

      console.log(`[AI Redacting] Starting search for: "${query}"`)
      const [lowerQuery] = normalize(query)
      let occurrences = []
      let count = 0
      const totalPages = this.pdfAppInstance.pdfDocument.numPages

      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        const page = await this.pdfAppInstance.pdfDocument.getPage(pageNum)
        const textContent = await page.getTextContent({ disableNormalization: true })
        const strBuf = []

        for (const textItem of textContent.items) {
          strBuf.push(textItem.str)
          if (textItem.hasEOL) {
            strBuf.push("\n")
          }
        }

        if (this.pageContents[pageNum - 1] === undefined) {
          [
            this.pageContents[pageNum - 1],
            this.pageDiffs[pageNum - 1],
            this.hasDiacritics[pageNum - 1],
          ] = normalize(strBuf.join(""))
        }

        const matcherResult = match(lowerQuery, this.pageContents[pageNum - 1], this.hasDiacritics[pageNum - 1])

        const matches = []
        const matchesLength = []
        const diffs = this.pageDiffs[pageNum - 1]

        matcherResult?.forEach(({ index, length }) => {
          const [matchPos, matchLen] = getOriginalIndex(diffs, index, length)
          if (matchLen) {
            matches.push(matchPos)
            matchesLength.push(matchLen)
          }
        })

        if (matches.length === 0) continue

        count += matches.length

        const pageElement = this.pdfAppInstance.pdfViewer.container.querySelector(`.page[data-page-number="${pageNum}"]`)
        let textLayer = pageElement.querySelector('.textLayer')

        if (textLayer === null) {
          let pageView = this.pdfAppInstance.pdfViewer.getPageView(pageNum - 1)
          pageView.generateTextLayer()
          await pageView.renderTextLayer()
          textLayer = pageView.textLayer.div
        }

        let pageView = this.pdfAppInstance.pdfViewer.getPageView(pageNum - 1)
        const { textDivs, textContentItemsStr } = pageView.textLayer.highlighter
        const convertedMatches = convertMatches(matches, matchesLength, textContentItemsStr)
        const rectangles = renderMatches(convertedMatches, textDivs, textLayer, pageNum)

        console.log(`[AI Redacting] Page ${pageNum} - Found ${matches.length} matches, created ${rectangles.length} rectangles`, rectangles)
        occurrences.push(...rectangles)
      }
      this.updateRedactedZones()

      console.log(`[AI Redacting] Search completed for "${query}" - Found ${count} occurrences across ${occurrences.length} rectangles`)
      console.log(`[AI Redacting] Sample rectangles:`, occurrences.slice(0, 3))
      this.searchResultsMap.set(query, { count, occurrences })
    },
    removeSearchResult(query) {
      const result = this.searchResultsMap.get(query)
      console.log(`[AI Redacting] Removing search result for "${query}" - ${result?.occurrences?.length || 0} rectangles removed`)
      this.searchResultsMap.delete(query)
      this.updateRedactedZones()
    },
    updateRedactedZones() {
      const allOccurrences = Array.from(this.searchResultsMap.values())
        .flatMap(result => result.occurrences)
      console.log(`[AI Redacting] Updating redacted zones - Total ${allOccurrences.length} rectangles from ${this.searchResultsMap.size} queries`)
      console.log(`[AI Redacting] searchResultsMap contents:`, Array.from(this.searchResultsMap.entries()).map(([query, data]) => ({ query, count: data.count, rects: data.occurrences.length })))
      console.log(`[AI Redacting] Emitting rectangles to parent:`, allOccurrences.length > 0 ? allOccurrences.slice(0, 3) : 'EMPTY ARRAY')
      
      this.$emit('updateRectangles', allOccurrences)
    },
  },
}
</script>

<style scoped>
.checkbox-label {
  max-width: 250px;
}
</style>
